/**
 * Per-merchant access derivation.
 *
 * access.ts already folds the entered merchant's read-only role into the route/
 * menu flags (it reads myRole out of localStorage); this hook is the in-render
 * view of the same `myRole`, for row-level gating and for pages that need a
 * fallback when reached by direct URL. A user who owns merchant A but only
 * holds a grant on merchant B gets different capabilities depending on which
 * merchant is entered. The backend enforces PartnerViewer read-only with 403s
 * either way — this is UX gating only.
 */
import { useAccess, useModel } from '@umijs/max';
import { isReadOnlyMerchantRole, ROLES } from '@/access';

export function useMerchantAccess() {
  const access = useAccess();
  const { currentMerchant } = useModel('merchant');

  const merchantRole = currentMerchant?.myRole;
  const isGrantedMerchant =
    merchantRole === ROLES.PARTNER_OWNER ||
    merchantRole === ROLES.PARTNER_VIEWER;

  // Read-only on the CURRENT merchant: granted viewer, or legacy merchant viewer.
  // Same predicate access.ts uses for menu visibility — keep them in one place.
  const isViewerOnCurrentMerchant = isReadOnlyMerchantRole(merchantRole);

  return {
    /** Current merchant's per-partner role: MerchantOwner / PartnerOwner / PartnerViewer / ... */
    merchantRole,
    /** Current merchant was granted by another owner (not owned by this user) */
    isGrantedMerchant,
    /** Write actions should be hidden for the current merchant (backend 403s them anyway) */
    isViewerOnCurrentMerchant,
    /** Only the merchant's real owner (or SuperAdmin/Admin acting-as) may grant/assign staff on it */
    canGrantCurrentMerchant:
      access.inMerchantMode || merchantRole === ROLES.MERCHANT_OWNER,
  };
}
